// Excel-like behaviour for the multi-tabular entry grids (designer live
// preview + standalone runtime).
//
// This module is deliberately framework-agnostic and DOM-driven: it attaches
// listeners to a grid container and reads the per-cell address attributes that
// the table renders:
//
//   data-grid-cell="<rowKey>::<columnKey>"
//   data-grid-row="<rowKey>"
//   data-grid-col="<columnKey>"
//
// It NEVER reaches into React state for the cell editors. Reads come from the
// rendered <input>/<select>/<textarea>; writes go back through the host's
// `writeCell(row, col, text)` callback, which coerces by column type and routes
// through the existing per-cell onChange so item_change cascades + validation
// still fire. That is what lets us add spreadsheet ergonomics "without
// disturbing existing functionality": the cell editors, Save All, audit
// columns, and resolve-defaults are all untouched.
//
// Features (all additive — each engages only on an unambiguous gesture):
//   • Range selection via shift+click and Ctrl/Cmd+Shift+Arrow (kept distinct
//     from in-cell text editing, since the cells stay always-editable inputs).
//   • Copy / Cut a selected range as TSV (Ctrl/Cmd+C / X) — only intercepted
//     when the selection spans more than one cell, so single-cell copy stays
//     native (copies the text in the focused input).
//   • Paste a TSV block (Ctrl/Cmd+V) starting at the active cell, growing the
//     row set as needed — only intercepted when the clipboard holds multi-cell
//     data (a tab or newline); a plain value pastes natively into the input.
//   • Fill-down / fill-right (Ctrl/Cmd+D / R) and a drag-fill handle.
//   • Clear a multi-cell selection with Delete/Backspace.
//   • Spreadsheet navigation: Ctrl/Cmd+Home/End, Ctrl/Cmd+Arrows (jump to
//     edge), Home/End (row ends), F2 (enter edit). Optional basic Arrow/Tab/
//     Enter nav for hosts that don't already supply it (the runtime grid).

export interface ExcelCellAddr {
  row: string
  col: string
}

export interface ExcelGridOptions {
  /** Master switch — when false attach() is a no-op. */
  enabled?: boolean
  /**
   * Provide basic Arrow / Tab / Enter cell navigation. The designer grid
   * already has its own `useGridKeyboardNav`, so it leaves this off; the
   * runtime grid turns it on.
   */
  basicNav?: boolean
  /** Render the bottom-right drag-fill handle (default true). */
  fillHandle?: boolean
  /**
   * Write a value into a logical cell. The host coerces `text` by column type
   * and routes it through the existing onChange (so item_change + validation
   * run). Disabled / read-only cells should be ignored by the host.
   */
  writeCell: (row: string, col: string, text: string) => void
  /**
   * Logical display value of a cell for copy / fill sources. Optional — falls
   * back to reading the rendered control's value.
   */
  readCell?: (row: string, col: string) => string | null
  /**
   * Ensure at least `count` rows exist (paste overflow). Returns once the rows
   * have been added; the grid is re-read from the DOM afterwards. Optional —
   * without it, paste clamps to the existing rows.
   */
  ensureRowCount?: (count: number) => Promise<unknown>
}

interface Rect { r0: number; r1: number; c0: number; c1: number }

const STYLE_ID = 'tw-excel-grid-style'
const SEL_CLASS = 'tw-xl-sel'
const ACTIVE_CLASS = 'tw-xl-active'
const PREVIEW_CLASS = 'tw-xl-fill-preview'

function ensureStylesheet(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return
  const style = doc.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
    [data-grid-cell].${SEL_CLASS} { box-shadow: inset 0 0 0 1px rgba(59,130,246,0.45); background: rgba(59,130,246,0.06); }
    [data-grid-cell].${ACTIVE_CLASS} { box-shadow: inset 0 0 0 1px rgba(59,130,246,0.65); }
    [data-grid-cell].${PREVIEW_CLASS} { box-shadow: inset 0 0 0 1px rgba(59,130,246,0.9); }
    .tw-xl-fill-handle {
      position: absolute; width: 8px; height: 8px;
      background: #3b82f6; border: 1px solid #fff; box-sizing: border-box;
      cursor: crosshair; z-index: 5; display: none;
    }
  `
  doc.head.appendChild(style)
}

function cssEscape(s: string): string {
  return s.replace(/(["\\])/g, '\\$1')
}

function parseAddr(el: HTMLElement | null): ExcelCellAddr | null {
  if (!el) return null
  const row = el.dataset.gridRow
  const col = el.dataset.gridCol
  if (!row || !col) return null
  return { row, col }
}

// Row + column keys in DOM order (the order the grid actually renders).
// Cells inside a NESTED grid (its own `[data-grid-scope]` root — e.g. an
// expanded child grid living inside this one) are excluded, so a parent grid
// never absorbs its child grid's cells into its own geometry.
function readGeometry(container: HTMLElement): { rows: string[]; cols: string[] } {
  const cells = container.querySelectorAll<HTMLElement>('[data-grid-cell]')
  const rows: string[] = []
  const cols: string[] = []
  const seenRow = new Set<string>()
  const seenCol = new Set<string>()
  cells.forEach((el) => {
    if (el.closest('[data-grid-scope]') !== container) return
    const r = el.dataset.gridRow
    const c = el.dataset.gridCol
    if (r && !seenRow.has(r)) { seenRow.add(r); rows.push(r) }
    if (c && !seenCol.has(c)) { seenCol.add(c); cols.push(c) }
  })
  return { rows, cols }
}

// Poll up to ~12 animation frames for the row count to reach `target` (used
// after a host grows the record set during a paste). Resolves with whatever
// rows exist once it reaches the target or the budget runs out.
function waitForRows(container: HTMLElement, target: number): Promise<string[]> {
  return new Promise((resolve) => {
    let tries = 0
    const tick = () => {
      const rows = readGeometry(container).rows
      if (rows.length >= target || tries >= 12) { resolve(rows); return }
      tries += 1
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  })
}

function findCell(container: HTMLElement, addr: ExcelCellAddr): HTMLElement | null {
  const sel = `[data-grid-cell="${cssEscape(`${addr.row}::${addr.col}`)}"]`
  return container.querySelector(sel) as HTMLElement | null
}

function cellControl(cell: HTMLElement): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null {
  return cell.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    'input, select, textarea',
  )
}

function domCellText(cell: HTMLElement): string {
  const ctrl = cellControl(cell)
  if (!ctrl) return (cell.textContent || '').trim()
  if (ctrl instanceof HTMLInputElement && ctrl.type === 'checkbox') {
    return ctrl.checked ? 'true' : 'false'
  }
  return ctrl.value ?? ''
}

function focusCellInput(cell: HTMLElement, selectAll = true): void {
  const ctrl = cellControl(cell) || cell.querySelector<HTMLElement>('button')
  if (!ctrl) return
  ctrl.focus()
  if (selectAll && ctrl instanceof HTMLInputElement
      && ['text', 'number', 'search', 'tel', 'url', 'email', 'date', 'datetime-local'].includes(ctrl.type)) {
    try { ctrl.select() } catch { /* ignore */ }
  }
}

export function attachExcelGrid(container: HTMLElement, opts: ExcelGridOptions): () => void {
  if (opts.enabled === false) return () => {}
  const doc = container.ownerDocument
  ensureStylesheet(doc)
  // Make the container programmatically focusable so a shift+click range
  // selection can receive the copy/cut keystroke even with no input focused.
  if (!container.hasAttribute('tabindex')) container.tabIndex = -1
  // Mark this container as a grid scope root. A cell belongs to THIS grid only
  // when its nearest `[data-grid-scope]` ancestor is this container — so a grid
  // nested inside another (an expanded child grid) doesn't have its cells or
  // events captured by the outer grid, and vice-versa.
  container.setAttribute('data-grid-scope', '')
  const ownsCell = (cell: HTMLElement | null | undefined): cell is HTMLElement =>
    !!cell && cell.closest('[data-grid-scope]') === container

  // Selection is two addresses: anchor (fixed corner) + active (moving corner).
  let anchor: ExcelCellAddr | null = null
  let active: ExcelCellAddr | null = null

  // Drag-fill state.
  let handle: HTMLDivElement | null = null
  if (opts.fillHandle !== false) {
    handle = doc.createElement('div')
    handle.className = 'tw-xl-fill-handle'
    // The container is the offset parent; the designer grid is already
    // position:relative and we set it for the runtime wrapper too.
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative'
    }
    container.appendChild(handle)
  }
  let fillDrag: { origin: Rect; preview: Rect | null } | null = null

  function rectFromSel(): Rect | null {
    if (!anchor || !active) return null
    const { rows, cols } = readGeometry(container)
    const ar = rows.indexOf(anchor.row), br = rows.indexOf(active.row)
    const ac = cols.indexOf(anchor.col), bc = cols.indexOf(active.col)
    if (ar < 0 || br < 0 || ac < 0 || bc < 0) return null
    return {
      r0: Math.min(ar, br), r1: Math.max(ar, br),
      c0: Math.min(ac, bc), c1: Math.max(ac, bc),
    }
  }

  function clearHighlight(): void {
    container.querySelectorAll<HTMLElement>(
      `.${SEL_CLASS}, .${ACTIVE_CLASS}, .${PREVIEW_CLASS}`,
    ).forEach((el) => {
      if (!ownsCell(el)) return // don't wipe a nested child grid's highlight
      el.classList.remove(SEL_CLASS, ACTIVE_CLASS, PREVIEW_CLASS)
    })
  }

  function applyHighlight(): void {
    clearHighlight()
    const rect = rectFromSel()
    if (!rect || !active) { positionHandle(null); return }
    const { rows, cols } = readGeometry(container)
    for (let r = rect.r0; r <= rect.r1; r++) {
      for (let c = rect.c0; c <= rect.c1; c++) {
        const cell = findCell(container, { row: rows[r], col: cols[c] })
        if (cell) cell.classList.add(SEL_CLASS)
      }
    }
    const activeCell = findCell(container, active)
    if (activeCell) activeCell.classList.add(ACTIVE_CLASS)
    positionHandle(rect)
  }

  function positionHandle(rect: Rect | null): void {
    if (!handle) return
    if (!rect) { handle.style.display = 'none'; return }
    const { rows, cols } = readGeometry(container)
    const brCell = findCell(container, { row: rows[rect.r1], col: cols[rect.c1] })
    if (!brCell) { handle.style.display = 'none'; return }
    const cRect = container.getBoundingClientRect()
    const bRect = brCell.getBoundingClientRect()
    const left = bRect.right - cRect.left + container.scrollLeft - 4
    const top = bRect.bottom - cRect.top + container.scrollTop - 4
    handle.style.left = `${left}px`
    handle.style.top = `${top}px`
    handle.style.display = 'block'
  }

  function setSelection(next: ExcelCellAddr | null, extend: boolean): void {
    active = next
    if (!extend || !anchor) anchor = next
    applyHighlight()
  }

  // ── Active-cell tracking ──────────────────────────────────────────
  // When a cell input gains focus (click or nav), it becomes the active cell
  // and — unless shift is held — collapses the selection to that single cell.
  const onFocusIn = (e: FocusEvent) => {
    const target = e.target as HTMLElement | null
    const cell = target?.closest<HTMLElement>('[data-grid-cell]')
    if (!ownsCell(cell)) return
    const addr = parseAddr(cell)
    if (!addr) return
    setSelection(addr, false)
  }

  // Shift+click extends the selection to the clicked cell without entering
  // text-edit there (so it doesn't fight the always-editable input).
  const onMouseDown = (e: MouseEvent) => {
    if (!e.shiftKey || !anchor) return
    const target = e.target as HTMLElement | null
    const cell = target?.closest<HTMLElement>('[data-grid-cell]')
    if (!ownsCell(cell)) return
    const addr = parseAddr(cell)
    if (!addr) return
    e.preventDefault()
    container.focus?.()
    setSelection(addr, true)
  }

  // ── Clipboard: copy / cut ─────────────────────────────────────────
  function selectionText(): string {
    const rect = rectFromSel()
    if (!rect) return ''
    const { rows, cols } = readGeometry(container)
    const lines: string[] = []
    for (let r = rect.r0; r <= rect.r1; r++) {
      const parts: string[] = []
      for (let c = rect.c0; c <= rect.c1; c++) {
        const row = rows[r], col = cols[c]
        const v = opts.readCell?.(row, col)
        if (v != null) { parts.push(v) }
        else {
          const cell = findCell(container, { row, col })
          parts.push(cell ? domCellText(cell) : '')
        }
      }
      lines.push(parts.join('\t'))
    }
    return lines.join('\n')
  }

  function isMultiCell(rect: Rect | null): boolean {
    return !!rect && (rect.r0 !== rect.r1 || rect.c0 !== rect.c1)
  }

  // True when a clipboard event came from within a grid cell or the container
  // itself — so a copy/paste in a sibling control (e.g. the search box that
  // shares the container) is left to the browser.
  function eventInGrid(e: Event): boolean {
    const t = e.target as HTMLElement | null
    if (!t) return false
    if (t === container) return true
    // Only when the originating cell belongs to THIS grid — a clipboard/key
    // event from a nested child grid's cell bubbles up here too, but the child
    // grid handles it; the parent must ignore it.
    return ownsCell(t.closest?.('[data-grid-cell]') as HTMLElement | null)
  }

  const onCopy = (e: ClipboardEvent) => {
    if (!eventInGrid(e)) return
    const rect = rectFromSel()
    if (!isMultiCell(rect)) return // single cell → native copy of input text
    e.preventDefault()
    e.clipboardData?.setData('text/plain', selectionText())
  }

  const onCut = (e: ClipboardEvent) => {
    if (!eventInGrid(e)) return
    const rect = rectFromSel()
    if (!isMultiCell(rect)) return
    e.preventDefault()
    e.clipboardData?.setData('text/plain', selectionText())
    clearSelectionValues()
  }

  // ── Clipboard: paste ──────────────────────────────────────────────
  function parseMatrix(text: string): string[][] {
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    let lines = normalized.split('\n')
    // Excel appends a trailing newline; drop a single empty last line.
    if (lines.length > 1 && lines[lines.length - 1] === '') lines = lines.slice(0, -1)
    return lines.map((l) => l.split('\t'))
  }

  async function pasteMatrix(matrix: string[][]): Promise<void> {
    if (!active || matrix.length === 0) return
    let { rows, cols } = readGeometry(container)
    const startR = rows.indexOf(active.row)
    const startC = cols.indexOf(active.col)
    if (startR < 0 || startC < 0) return

    const neededRows = startR + matrix.length
    if (neededRows > rows.length && opts.ensureRowCount) {
      try { await opts.ensureRowCount(neededRows) } catch { /* clamp below */ }
      // The host grows the row set via React state, so the new <tr>s land on a
      // later commit — poll a few frames until they're in the DOM (or give up
      // and clamp to whatever exists).
      rows = await waitForRows(container, neededRows)
      cols = readGeometry(container).cols
    }

    for (let i = 0; i < matrix.length; i++) {
      const r = startR + i
      if (r >= rows.length) break
      const line = matrix[i]
      for (let j = 0; j < line.length; j++) {
        const c = startC + j
        if (c >= cols.length) break
        opts.writeCell(rows[r], cols[c], line[j])
      }
    }

    // Reselect the pasted block so the user sees what landed.
    const lastR = Math.min(startR + matrix.length - 1, rows.length - 1)
    const maxLineLen = matrix.reduce((m, l) => Math.max(m, l.length), 1)
    const lastC = Math.min(startC + maxLineLen - 1, cols.length - 1)
    anchor = { row: rows[startR], col: cols[startC] }
    active = { row: rows[lastR], col: cols[lastC] }
    // Defer highlight until React has rendered any new rows.
    requestAnimationFrame(applyHighlight)
  }

  const onPaste = (e: ClipboardEvent) => {
    if (!eventInGrid(e)) return
    const text = e.clipboardData?.getData('text/plain') ?? ''
    // Only intercept genuine multi-cell pastes; a plain value pastes natively
    // into the focused input (preserving normal typing UX).
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return
    if (!active) return
    e.preventDefault()
    void pasteMatrix(parseMatrix(text))
  }

  // ── Fill / clear ──────────────────────────────────────────────────
  function clearSelectionValues(): void {
    const rect = rectFromSel()
    if (!rect) return
    const { rows, cols } = readGeometry(container)
    for (let r = rect.r0; r <= rect.r1; r++) {
      for (let c = rect.c0; c <= rect.c1; c++) {
        opts.writeCell(rows[r], cols[c], '')
      }
    }
  }

  function sourceValue(row: string, col: string): string {
    const v = opts.readCell?.(row, col)
    if (v != null) return v
    const cell = findCell(container, { row, col })
    return cell ? domCellText(cell) : ''
  }

  function fillDown(): void {
    const rect = rectFromSel()
    const { rows, cols } = readGeometry(container)
    if (rect && rect.r1 > rect.r0) {
      // Fill the selection's top row down through the block.
      for (let c = rect.c0; c <= rect.c1; c++) {
        const src = sourceValue(rows[rect.r0], cols[c])
        for (let r = rect.r0 + 1; r <= rect.r1; r++) {
          opts.writeCell(rows[r], cols[c], src)
        }
      }
    } else if (active) {
      // Single cell: copy the value directly above (Excel Ctrl+D).
      const r = rows.indexOf(active.row)
      const c = cols.indexOf(active.col)
      if (r > 0 && c >= 0) {
        opts.writeCell(rows[r], cols[c], sourceValue(rows[r - 1], cols[c]))
      }
    }
  }

  function fillRight(): void {
    const rect = rectFromSel()
    const { rows, cols } = readGeometry(container)
    if (rect && rect.c1 > rect.c0) {
      for (let r = rect.r0; r <= rect.r1; r++) {
        const src = sourceValue(rows[r], cols[rect.c0])
        for (let c = rect.c0 + 1; c <= rect.c1; c++) {
          opts.writeCell(rows[r], cols[c], src)
        }
      }
    } else if (active) {
      const r = rows.indexOf(active.row)
      const c = cols.indexOf(active.col)
      if (c > 0 && r >= 0) {
        opts.writeCell(rows[r], cols[c], sourceValue(rows[r], cols[c - 1]))
      }
    }
  }

  // Tile the `origin` block across the (larger) `target` block — the drag-fill
  // gesture. Cells already inside `origin` are left as-is.
  function applyDragFill(origin: Rect, target: Rect): void {
    const { rows, cols } = readGeometry(container)
    const srcH = origin.r1 - origin.r0 + 1
    const srcW = origin.c1 - origin.c0 + 1
    // Snapshot the source values before any writes mutate them.
    const src: string[][] = []
    for (let r = 0; r < srcH; r++) {
      const line: string[] = []
      for (let c = 0; c < srcW; c++) {
        line.push(sourceValue(rows[origin.r0 + r], cols[origin.c0 + c]))
      }
      src.push(line)
    }
    for (let r = target.r0; r <= target.r1; r++) {
      for (let c = target.c0; c <= target.c1; c++) {
        if (r >= origin.r0 && r <= origin.r1 && c >= origin.c0 && c <= origin.c1) continue
        const sv = src[(r - origin.r0 + srcH * 1000) % srcH][(c - origin.c0 + srcW * 1000) % srcW]
        opts.writeCell(rows[r], cols[c], sv)
      }
    }
  }

  // ── Drag-fill handle ──────────────────────────────────────────────
  const onHandleDown = (e: MouseEvent) => {
    const origin = rectFromSel()
    if (!origin) return
    e.preventDefault()
    e.stopPropagation()
    fillDrag = { origin, preview: null }
    doc.addEventListener('mousemove', onHandleMove, true)
    doc.addEventListener('mouseup', onHandleUp, true)
  }

  function previewHighlight(target: Rect): void {
    container.querySelectorAll<HTMLElement>(`.${PREVIEW_CLASS}`)
      .forEach((el) => el.classList.remove(PREVIEW_CLASS))
    const { rows, cols } = readGeometry(container)
    for (let r = target.r0; r <= target.r1; r++) {
      for (let c = target.c0; c <= target.c1; c++) {
        const cell = findCell(container, { row: rows[r], col: cols[c] })
        if (cell) cell.classList.add(PREVIEW_CLASS)
      }
    }
  }

  const onHandleMove = (e: MouseEvent) => {
    if (!fillDrag) return
    const el = doc.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null
    const cell = el?.closest<HTMLElement>('[data-grid-cell]')
    if (!ownsCell(cell)) return
    const addr = parseAddr(cell)
    if (!addr) return
    const { rows, cols } = readGeometry(container)
    const r = rows.indexOf(addr.row)
    const c = cols.indexOf(addr.col)
    const o = fillDrag.origin
    // Extend along the dominant axis only (Excel-like).
    const dDown = Math.max(0, r - o.r1)
    const dRight = Math.max(0, c - o.c1)
    const target: Rect = dDown >= dRight
      ? { r0: o.r0, r1: Math.max(o.r1, r), c0: o.c0, c1: o.c1 }
      : { r0: o.r0, r1: o.r1, c0: o.c0, c1: Math.max(o.c1, c) }
    fillDrag.preview = target
    previewHighlight(target)
  }

  const onHandleUp = () => {
    doc.removeEventListener('mousemove', onHandleMove, true)
    doc.removeEventListener('mouseup', onHandleUp, true)
    const drag = fillDrag
    fillDrag = null
    if (!drag || !drag.preview) { applyHighlight(); return }
    applyDragFill(drag.origin, drag.preview)
    const { rows, cols } = readGeometry(container)
    anchor = { row: rows[drag.preview.r0], col: cols[drag.preview.c0] }
    active = { row: rows[drag.preview.r1], col: cols[drag.preview.c1] }
    requestAnimationFrame(applyHighlight)
  }

  // ── Keyboard ──────────────────────────────────────────────────────
  function moveActive(toRow: number, toCol: number, extend: boolean): void {
    const { rows, cols } = readGeometry(container)
    const r = Math.max(0, Math.min(rows.length - 1, toRow))
    const c = Math.max(0, Math.min(cols.length - 1, toCol))
    const addr = { row: rows[r], col: cols[c] }
    setSelection(addr, extend)
    const cell = findCell(container, addr)
    if (cell && !extend) focusCellInput(cell)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null
    if (!target) return
    const cell = target.closest<HTMLElement>('[data-grid-cell]')
    // Allow handle-driven ops even if focus is on the container itself.
    // A keystroke from a nested child grid's cell bubbles here too — ignore it
    // (the child grid owns it).
    const inGrid = ownsCell(cell) || target === container
    if (!inGrid) return
    const addr = cell ? parseAddr(cell) : active
    if (!addr) return

    const mod = e.ctrlKey || e.metaKey
    const isInput = target instanceof HTMLInputElement
    const isTextArea = target instanceof HTMLTextAreaElement
    const caretAtStart = isInput && target.selectionStart === 0 && target.selectionEnd === 0
    const caretAtEnd = isInput
      && target.selectionStart === target.value.length
      && target.selectionEnd === target.value.length
    const { rows, cols } = readGeometry(container)
    const rIdx = rows.indexOf(addr.row)
    const cIdx = cols.indexOf(addr.col)

    // Copy / cut / paste ride on the native clipboard events (onCopy/onCut/
    // onPaste) — they fire on Ctrl/Cmd+C/X/V and let us set/read clipboard
    // data synchronously without a permissions prompt.

    // ── fill ──
    if (mod && (e.key === 'd' || e.key === 'D')) { e.preventDefault(); fillDown(); return }
    if (mod && (e.key === 'r' || e.key === 'R')) { e.preventDefault(); fillRight(); return }

    // ── clear a multi-cell selection ──
    if ((e.key === 'Delete' || e.key === 'Backspace') && isMultiCell(rectFromSel())) {
      e.preventDefault(); clearSelectionValues(); return
    }

    // ── F2: enter edit on the active cell ──
    if (e.key === 'F2') {
      e.preventDefault()
      const ac = findCell(container, addr)
      if (ac) focusCellInput(ac, false)
      return
    }

    // ── Ctrl/Cmd + Home / End: jump to grid corners ──
    if (mod && e.key === 'Home') {
      e.preventDefault(); moveActive(0, 0, e.shiftKey); return
    }
    if (mod && e.key === 'End') {
      e.preventDefault(); moveActive(rows.length - 1, cols.length - 1, e.shiftKey); return
    }

    // ── Ctrl/Cmd + Arrow: jump to the edge in that direction ──
    if (mod && e.key === 'ArrowUp') { e.preventDefault(); moveActive(0, cIdx, e.shiftKey); return }
    if (mod && e.key === 'ArrowDown') { e.preventDefault(); moveActive(rows.length - 1, cIdx, e.shiftKey); return }
    if (mod && e.key === 'ArrowLeft') { e.preventDefault(); moveActive(rIdx, 0, e.shiftKey); return }
    if (mod && e.key === 'ArrowRight') { e.preventDefault(); moveActive(rIdx, cols.length - 1, e.shiftKey); return }

    // ── Shift + Arrow: extend selection (only when not consuming text) ──
    if (e.shiftKey && !mod && !isTextArea) {
      const canExtendH = !isInput || target.type === 'checkbox'
        || (e.key === 'ArrowLeft' && caretAtStart) || (e.key === 'ArrowRight' && caretAtEnd)
      if (e.key === 'ArrowUp') { e.preventDefault(); moveActive(rIdx - 1, cIdx, true); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); moveActive(rIdx + 1, cIdx, true); return }
      if (e.key === 'ArrowLeft' && canExtendH) { e.preventDefault(); moveActive(rIdx, cIdx - 1, true); return }
      if (e.key === 'ArrowRight' && canExtendH) { e.preventDefault(); moveActive(rIdx, cIdx + 1, true); return }
    }

    // ── Home / End: row ends (guarded for in-cell text editing) ──
    if (!mod && e.key === 'Home' && (!isInput || caretAtStart) && !isTextArea) {
      e.preventDefault(); moveActive(rIdx, 0, e.shiftKey); return
    }
    if (!mod && e.key === 'End' && (!isInput || caretAtEnd) && !isTextArea) {
      e.preventDefault(); moveActive(rIdx, cols.length - 1, e.shiftKey); return
    }

    // ── Optional basic navigation (runtime grid only) ──
    if (opts.basicNav && !mod) basicNavKey(e, target, addr, rows, cols, caretAtStart, caretAtEnd)
  }

  // Ported, guarded Arrow/Tab/Enter navigation for hosts without their own.
  function basicNavKey(
    e: KeyboardEvent, target: HTMLElement, addr: ExcelCellAddr,
    rows: string[], cols: string[], caretAtStart: boolean, caretAtEnd: boolean,
  ): void {
    if (target instanceof HTMLTextAreaElement) return
    const rIdx = rows.indexOf(addr.row)
    const cIdx = cols.indexOf(addr.col)
    if (rIdx < 0 || cIdx < 0) return
    const isInput = target instanceof HTMLInputElement
    let next: ExcelCellAddr | null = null

    switch (e.key) {
      case 'Escape': target.blur(); return
      case 'Tab': {
        let nr = rIdx, nc = cIdx + (e.shiftKey ? -1 : 1)
        if (nc >= cols.length) { nc = 0; nr = Math.min(rIdx + 1, rows.length - 1) }
        else if (nc < 0) { nc = cols.length - 1; nr = Math.max(rIdx - 1, 0) }
        next = { row: rows[nr], col: cols[nc] }
        break
      }
      case 'Enter': {
        if (e.altKey) return
        const nr = e.shiftKey ? Math.max(rIdx - 1, 0) : Math.min(rIdx + 1, rows.length - 1)
        if (nr === rIdx) return
        next = { row: rows[nr], col: addr.col }
        break
      }
      case 'ArrowUp':
      case 'ArrowDown': {
        if (e.shiftKey) return
        if (target instanceof HTMLSelectElement) return
        if (isInput && ['number', 'date', 'datetime-local', 'time'].includes(target.type)) return
        const nr = Math.max(0, Math.min(rows.length - 1, rIdx + (e.key === 'ArrowUp' ? -1 : 1)))
        if (nr === rIdx) return
        next = { row: rows[nr], col: addr.col }
        break
      }
      case 'ArrowLeft':
      case 'ArrowRight': {
        if (e.shiftKey) return
        if (isInput && target.type !== 'checkbox') {
          if (e.key === 'ArrowLeft' && !caretAtStart) return
          if (e.key === 'ArrowRight' && !caretAtEnd) return
        }
        const nc = Math.max(0, Math.min(cols.length - 1, cIdx + (e.key === 'ArrowLeft' ? -1 : 1)))
        if (nc === cIdx) return
        next = { row: addr.row, col: cols[nc] }
        break
      }
      default: return
    }
    e.preventDefault()
    if (next) {
      const cell = findCell(container, next)
      if (cell) { setSelection(next, false); focusCellInput(cell) }
    }
  }

  // Keep the highlight + handle aligned when rows are added/removed (paste
  // growth) or the grid scrolls.
  const observer = new MutationObserver(() => {
    if (active && !findCell(container, active)) { anchor = null; active = null; clearHighlight() }
    else applyHighlight()
  })
  observer.observe(container, { childList: true, subtree: true })
  const onScroll = () => positionHandle(rectFromSel())

  // ── wire up ───────────────────────────────────────────────────────
  container.addEventListener('focusin', onFocusIn)
  container.addEventListener('mousedown', onMouseDown, true)
  container.addEventListener('keydown', onKeyDown)
  container.addEventListener('copy', onCopy)
  container.addEventListener('cut', onCut)
  container.addEventListener('paste', onPaste)
  container.addEventListener('scroll', onScroll, true)
  handle?.addEventListener('mousedown', onHandleDown)

  return () => {
    observer.disconnect()
    container.removeEventListener('focusin', onFocusIn)
    container.removeEventListener('mousedown', onMouseDown, true)
    container.removeEventListener('keydown', onKeyDown)
    container.removeEventListener('copy', onCopy)
    container.removeEventListener('cut', onCut)
    container.removeEventListener('paste', onPaste)
    container.removeEventListener('scroll', onScroll, true)
    handle?.removeEventListener('mousedown', onHandleDown)
    doc.removeEventListener('mousemove', onHandleMove, true)
    doc.removeEventListener('mouseup', onHandleUp, true)
    handle?.remove()
    clearHighlight()
    container.removeAttribute('data-grid-scope')
  }
}
